import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import Ansi from "@cocalc/ansi-to-react";
import {
  DownloadIcon as Download,
  Modal,
  ScrollDownIcon,
  ScrollTopIcon,
  ShareIcon as Share,
  Typography
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import stripAnsi from "strip-ansi";

import { ACTIVITY_TYPES } from "Constants/constants";
import { Button, CopyButton } from "ds/Button";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import client from "Libs/platform";
import { downloadBlob } from "Libs/utils";
import { selectProjectActivityById } from "Reducers/activity";
import { loadLogFromActivity, Log } from "Reducers/log";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { gitProjectSelector } from "Reducers/project/project";
import ActivityHeader from "src/organization/common/components/activities/Activity/ActivityHeader";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./LogModal.styles";

import type { Activity } from "@packages/client";

const logs2Text = (logs: Log[]) => {
  return logs.reduce((logText, jsLog) => {
    if (!jsLog?.data) {
      return logText;
    }
    return `${logText} ${stripAnsi(jsLog.data.message)}`;
  }, "");
};

const LogModal = ({
  initialActivity,
  closeModal
}: {
  initialActivity?: Activity;
  closeModal: () => void;
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  const dispatch = useAppDispatch();
  const logWrapper = useRef<HTMLPreElement>(null);
  const listLog = useRef<VirtuosoHandle>(null);
  const { activityId, projectId, organizationId } = useDecodedParams<{
    activityId: string;
    projectId: string;
    organizationId: string;
  }>();

  const [activity, setActivity] = useState(initialActivity);
  const [linesSelected, setLinesSelected] = useState<{
    start: null | number;
    end: null | number;
    scrollTo: boolean;
  }>({
    start: null,
    end: null,
    scrollTo: false
  });
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const [selection, setSelection] = useState<string>();
  const [displayScrollBtns, setDisplayScrollBtns] = useState({
    toTop: false,
    toBottom: true
  });
  const [visibleIndex, setVisibleIndex] = useState({
    startIndex: 0,
    endIndex: 0
  });
  const [loadedLog, setLoadedLog] = useState(false);

  const jsLog = useAppSelector(({ log }) =>
    activity ? (log[activity.id]?.log ?? []) : []
  );
  const loading = useAppSelector(({ log }) =>
    activity ? (log[activity.id]?.loading ?? true) : true
  );

  const logStreamstreamEnded = useAppSelector(({ log }) =>
    activity ? log[activity.id]?.streamEnded : undefined
  );

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId,
      projectId
    })
  );
  const activityUpdates = useAppSelector(state =>
    selectProjectActivityById(state, {
      activityId,
      organizationId,
      projectId
    })
  );

  useEffect(() => {
    const regex = /^#L(\d*)(-L(\d*))?/;
    const match = hash.match(regex);
    if (match) {
      setLinesSelected({
        start: Number(match[1]) - 1,
        end: match[3] ? Number(match[3]) - 1 : null,
        scrollTo: true
      });
    }
  }, [hash]);

  useEffect(() => {
    const loadActivity = async () => {
      const result = await client.getProjectActivity(projectId, activityId);
      setActivity(result);
    };
    if (activityId) loadActivity();
  }, [activityId, projectId]);

  useEffect(() => {
    if (activityUpdates) setActivity(activityUpdates);
  }, [activityUpdates]);

  useEffect(() => {
    if (!loadedLog && activity?.id) {
      dispatch(loadLogFromActivity({ activity }));
      setLoadedLog(true);
    }
  }, [activity, dispatch, loadedLog]);

  useEffect(() => {
    const { start, end } = linesSelected;
    if (start !== null || end !== null) {
      let newHash = "#";
      if (start !== null) newHash += `L${start + 1}`;
      if (end !== null) newHash += `-L${end + 1}`;
      if (hash !== newHash)
        navigate({ hash: newHash, search }, { replace: true });
    }
  }, [hash, linesSelected, navigate, search]);

  const itemCount = useMemo(() => {
    if (!jsLog.length) return 0;
    const length = jsLog.length;
    return loading ? length + 1 : length;
  }, [jsLog.length, loading]);

  useEffect(() => {
    setDisplayScrollBtns({
      toTop: visibleIndex.startIndex > 0,
      toBottom: visibleIndex.endIndex + 1 < itemCount
    });
  }, [itemCount, visibleIndex]);

  const onRowsRendered = ({
    startIndex,
    endIndex
  }: {
    startIndex: number;
    endIndex: number;
  }) => {
    setVisibleIndex({ startIndex, endIndex });
  };

  const fullLogs = useMemo(() => {
    if (!jsLog.length) return "";
    return logs2Text(jsLog);
  }, [jsLog]);

  const copyText = useMemo(() => {
    const { start, end } = linesSelected;

    // If user selects lines
    if (selection) return selection;

    // When only one line is selected
    if (typeof start === "number" && !end && jsLog[start]) {
      return stripAnsi(jsLog[start].data.message);
    }

    // When multilines are selected
    if (typeof start === "number" && end) {
      return logs2Text(jsLog.slice(start, end + 1));
    }

    return fullLogs;
  }, [linesSelected, selection, jsLog, fullLogs]);

  const offsetHeight = logWrapper.current?.offsetHeight ?? 0;
  const height = offsetHeight > 0 ? offsetHeight - 10 : 50;

  const selectLine = (e: React.MouseEvent, line: number) => {
    e.preventDefault();
    e.stopPropagation();
    const { start, end } = linesSelected;

    if (start === line) {
      setLinesSelected({ start: end, end: null, scrollTo: false });
      return;
    }

    if (e.shiftKey) {
      if (!start && !end) {
        setLinesSelected({ start: line, end: null, scrollTo: false });
      } else if (line < start!) {
        setLinesSelected({ start: line, end: start, scrollTo: false });
      } else {
        setLinesSelected({ ...linesSelected, end: line, scrollTo: false });
      }
    } else {
      setLinesSelected({ start: line, end: null, scrollTo: false });
    }
  };

  const handleSelect = (e: React.MouseEvent<"div">) => {
    e.preventDefault();
    setSelection(window.getSelection()?.toString());
  };

  const isSelected = (index: number) => {
    const { start, end } = linesSelected;
    if (!end) {
      return start === index;
    }
    return start! <= index && index <= end;
  };

  const showTimeStamp = (index: number) => {
    const { start, end } = linesSelected;
    return index === start || index === end;
  };

  const [logs, selectedText] = [
    fullLogs,
    // Remove Line numbers from copied build logs
    copyText.replaceAll(/\n(\d+)(.*)/gi, "\n$2")
  ];

  const onDownloadLog = () => {
    downloadBlob(
      selectedText || logs,
      { type: '"data:attachment/text"' },
      `${kindOfLog}.txt`
    );
  };

  const scrollTo = useCallback(
    (index: number | "LAST") => {
      listLog.current?.scrollToIndex({
        index,
        align: "center"
      });
      setLinesSelected({ ...linesSelected, scrollTo: false });
    },
    [linesSelected]
  );

  useEffect(() => {
    if (!hasScrolledDown && !loading && activity?.result === "failure") {
      setHasScrolledDown(true);
    }
  }, [activity?.result, hasScrolledDown, linesSelected, loading, scrollTo]);

  useEffect(() => {
    if (linesSelected.scrollTo && !loading) {
      scrollTo(linesSelected.start ?? 0);
    }
  }, [linesSelected, loading, scrollTo]);

  const rowRenderer = (index: number, log: Log | undefined) => {
    const isError = log?.data.message.trim().startsWith("E: ");

    return (
      <>
        {log ? (
          <S.Line
            selected={isSelected(index)}
            showTimeStamp={showTimeStamp(index)}
            $isError={isError}
          >
            <S.LineNumber onClick={e => selectLine(e, index)}>
              {index + 1}
            </S.LineNumber>
            <Ansi>{log.data.message}</Ansi>
            <S.TimeStamp>
              {index !== linesSelected.end ? (
                <>{moment(log.data.timestamp).format("LTS")}</>
              ) : (
                <>
                  {moment
                    .duration(
                      moment(log.data.timestamp).diff(
                        moment(jsLog[linesSelected.start!]?.data?.timestamp)
                      )
                    )
                    .asSeconds()}{" "}
                  sec
                </>
              )}
            </S.TimeStamp>
          </S.Line>
        ) : (
          <S.Spacer />
        )}
      </>
    );
  };

  const kindOfLog = useMemo(
    () =>
      [ACTIVITY_TYPES.MAINTENANCE.UPGRADE as string].includes(
        activity?.type as string
      )
        ? "changelog"
        : "build_log",
    [activity?.type]
  );

  return (
    <Modal
      isOpen
      autoFocus
      size="custom"
      height="100%"
      handleClose={closeModal}
    >
      <S.Header>
        <Typography.Title tag="h3">
          {[project?.name, activity?.environments[0]]
            .filter(item => item)
            .join(" / ")}
        </Typography.Title>
        <S.Org $color="neutral-600">{organization?.label}</S.Org>
      </S.Header>
      <S.BuildLogHeader>
        {activity && (
          <ActivityHeader activity={activity} organization={organization} />
        )}
      </S.BuildLogHeader>
      {activity && (
        <S.ActivitiyActionContainer>
          <S.LogTitle>{intl.formatMessage({ id: kindOfLog })}</S.LogTitle>
          {logStreamstreamEnded && !!logs.length && (
            <S.ActionWrapper>
              <Tooltip
                tooltip={intl.formatMessage({ id: "copy_build_log" })}
                align="center"
                analyticId="copy_build_log"
              >
                <CopyButton
                  text={selectedText || logs}
                  withText={false}
                  variant="secondary"
                />
              </Tooltip>

              <Tooltip
                tooltip={intl.formatMessage({ id: "download_build_log" })}
                align="center"
                analyticId="download_build_log"
              >
                <Button
                  analyticId="download"
                  content="icon"
                  onClick={onDownloadLog}
                  variant="secondary"
                >
                  <Download />
                </Button>
              </Tooltip>

              <Tooltip
                tooltip={intl.formatMessage({ id: "copy_build_url" })}
                align="center"
                analyticId="copy_build_url"
              >
                <CopyButton
                  analyticId="copy"
                  text={window.location.href}
                  variant="secondary"
                  withText={false}
                  icon={<Share />}
                />
              </Tooltip>

              <S.ScrollButtons>
                <Tooltip
                  tooltip={intl.formatMessage({ id: "scroll_top" })}
                  align="center"
                  analyticId="scroll_top"
                >
                  <Button
                    analyticId="scroll_top"
                    onClick={e => {
                      e.preventDefault();
                      scrollTo(0);
                    }}
                    variant="secondary"
                    content="icon"
                    disabled={!displayScrollBtns.toTop}
                  >
                    <ScrollTopIcon />
                  </Button>
                </Tooltip>

                <Tooltip
                  tooltip={intl.formatMessage({ id: "scroll_down" })}
                  align="center"
                  analyticId="scroll_down"
                >
                  <Button
                    analyticId="scroll_down"
                    onClick={e => {
                      e.preventDefault();
                      scrollTo(itemCount - 1);
                    }}
                    variant="secondary"
                    content="icon"
                    disabled={!displayScrollBtns.toBottom}
                  >
                    <ScrollDownIcon />
                  </Button>
                </Tooltip>
              </S.ScrollButtons>
            </S.ActionWrapper>
          )}
        </S.ActivitiyActionContainer>
      )}
      {project && activity && (
        <S.LogPre
          ref={logWrapper}
          showLinesNumber={!loading && logs?.length > 0}
        >
          <S.Container>
            {loading ? (
              <S.DotDotDot>{intl.formatMessage({ id: "loading" })}</S.DotDotDot>
            ) : logs?.length > 0 ? (
              <Virtuoso
                ref={listLog}
                style={{ height: height }}
                data={jsLog}
                itemContent={rowRenderer}
                onMouseUp={handleSelect}
                overscan={Infinity}
                rangeChanged={onRowsRendered}
              />
            ) : (
              <S.Empty>{intl.formatMessage({ id: "log_empty" })}</S.Empty>
            )}
          </S.Container>
        </S.LogPre>
      )}
    </Modal>
  );
};

export default LogModal;
